// src/components/Workspace/CollaboratorWorkspaceAccess.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useUserContext } from '../../contexts/UserContext';

/**
 * Component that handles access to workspaces for collaborator accounts
 * This component will:
 * 1. Check if the current user is a collaborator
 * 2. Find all workspaces where they are a member
 * 3. Load and display those workspaces
 * 4. Allow the user to select which workspace to access
 */
function CollaboratorWorkspaceAccess() {
  const { 
    user, 
    loading, 
    isCollaborator, 
    collaboratorWorkspaces, 
    activeBusinessProfileId,
    switchCollaboratorWorkspace,
    isWorkspaceSwitching
  } = useUserContext();
  
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [accessError, setAccessError] = useState(null);
  const [ownerNames, setOwnerNames] = useState({});
  const navigate = useNavigate();

  // Load owner names for all workspaces
  useEffect(() => {
    const loadOwnerNames = async () => {
      if (!collaboratorWorkspaces || collaboratorWorkspaces.length === 0) {
        setIsCheckingAccess(false);
        return;
      }
      
      const names = {};
      const uniqueOwnerIds = [...new Set(collaboratorWorkspaces.map(w => w.ownerUid))];
      
      for (const ownerId of uniqueOwnerIds) {
        try {
          const ownerDoc = await getDoc(doc(db, 'users', ownerId));
          if (ownerDoc.exists()) {
            const ownerData = ownerDoc.data();
            names[ownerId] = ownerData.name || ownerData.displayName || 'Workspace Owner';
          } else {
            names[ownerId] = 'Workspace Owner';
          }
        } catch (error) {
          console.error(`Error loading owner name for ${ownerId}:`, error);
          names[ownerId] = 'Workspace Owner';
        }
      }
      
      setOwnerNames(names);
      
      // Find the currently active workspace
      const active = collaboratorWorkspaces.find(w => w.id === activeBusinessProfileId);
      if (active) {
        setSelectedWorkspace(active);
      } else if (collaboratorWorkspaces.length > 0) {
        setSelectedWorkspace(collaboratorWorkspaces[0]);
      }
      
      setIsCheckingAccess(false);
    };
    
    loadOwnerNames();
  }, [collaboratorWorkspaces, activeBusinessProfileId]);

  // Function to switch to a workspace
  const handleSelectWorkspace = async (workspace) => {
    if (isWorkspaceSwitching) return;
    
    setSelectedWorkspace(workspace);
    const success = await switchCollaboratorWorkspace(workspace.id);
    
    if (!success) {
      setAccessError('Failed to switch to the selected workspace. Please try again.');
    }
  };

  // Function to navigate to various pages
  const navigateTo = (path) => {
    navigate(path);
  };

  // Show loading state
  if (loading || isCheckingAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <p className="text-gray-600">Loading your workspaces...</p>
      </div>
    );
  }

  // Show error state
  if (accessError) {
    return (
      <div className="bg-white shadow rounded-lg p-6 max-w-md mx-auto mt-8">
        <div className="flex flex-col items-center text-center">
          <div className="bg-red-100 text-red-600 p-3 rounded-full mb-4">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2">Workspace Access Error</h2>
          <p className="text-gray-600 mb-4">{accessError}</p>
          <button 
            onClick={() => setAccessError(null)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No workspaces available
  if (!collaboratorWorkspaces || collaboratorWorkspaces.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-6 max-w-md mx-auto mt-8">
        <div className="flex flex-col items-center text-center">
          <div className="bg-yellow-100 text-yellow-600 p-3 rounded-full mb-4">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2">No Workspaces Found</h2>
          <p className="text-gray-600 mb-4">
            You don't have access to any workspaces yet. If you were invited to a workspace,
            please contact the workspace owner.
          </p>
          <button 
            onClick={() => navigateTo('/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6 max-w-lg mx-auto mt-8">
      <h2 className="text-xl font-semibold mb-4">Your Workspaces</h2>
      
      <div className="space-y-4">
        {collaboratorWorkspaces.map((workspace) => (
          <div 
            key={`${workspace.ownerUid}-${workspace.id}`}
            className={`border rounded-lg p-4 cursor-pointer transition-colors ${
              selectedWorkspace && selectedWorkspace.id === workspace.id ? 
              'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
            }`}
            onClick={() => setSelectedWorkspace(workspace)}
          >
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-medium">{workspace.profileName}</h3>
                <p className="text-sm text-gray-500">
                  Owner: {ownerNames[workspace.ownerUid] || 'Workspace Owner'}
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
                    {workspace.role === 'owner' ? 'Owner' : 'Collaborator'}
                  </span>
                </p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectWorkspace(workspace);
                }}
                disabled={isWorkspaceSwitching}
                className={`px-3 py-1 text-sm rounded ${
                  selectedWorkspace && selectedWorkspace.id === workspace.id 
                  ? 'bg-green-600 text-white' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
                } ${isWorkspaceSwitching ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {selectedWorkspace && selectedWorkspace.id === workspace.id 
                  ? 'Selected' 
                  : 'Select'}
              </button>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-6 flex space-x-4 justify-center">
        <button
          onClick={() => navigateTo('/dashboard')}
          className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          Go to Dashboard
        </button>
        
        <button
          onClick={() => navigateTo('/workspace')}
          disabled={!selectedWorkspace}
          className={`px-4 py-2 rounded flex items-center ${
            selectedWorkspace 
            ? 'bg-blue-600 text-white hover:bg-blue-700' 
            : 'bg-blue-200 text-blue-800 cursor-not-allowed'
          }`}
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          Manage Workspace
        </button>
      </div>
      
      <div className="mt-6 bg-blue-50 p-4 rounded-lg text-sm text-blue-800">
        <p className="flex items-start">
          <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>
            You have access to <strong>{collaboratorWorkspaces.length}</strong> workspace{collaboratorWorkspaces.length !== 1 ? 's' : ''}. 
            Select a workspace to view its data. Each workspace contains data specific to that organization.
          </span>
        </p>
      </div>
    </div>
  );
}

export default CollaboratorWorkspaceAccess;